import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@core/store';
import { NAVBAR_HEIGHT, SIDEBAR_WIDTH } from '@layouts/main-layout/constants';
import useTopBannersOffset from '@layouts/main-layout/hooks/useTopBannersOffset';

import CarouselDots from './CarouselDots';
import { BANNER_HEIGHT, SLIDE_DURATION_MS } from './constants';
import DynamicBanner from './DynamicBanner';
import useBannerActions from './useBannerActions';
import useBannerCarousel from './useBannerCarousel';

import { getCustomActionComponent } from '../../constants/customActionRegistry';
import useTrackDynamicView from '../../hooks/useTrackDynamicView';
import {
  selectAllBanners,
  setBannerIndex
} from '../../slices/dynamicComponentsSlice';
import { ComponentType } from '../../types';

const DynamicBannerCarousel = () => {
  const dispatch = useAppDispatch();
  const { total: topBannersOffset } = useTopBannersOffset();

  const banners = useAppSelector(selectAllBanners);
  const { bannerIndex } = useAppSelector((state) => state.dynamicComponents);
  const count = banners.length;
  const currentIndex = Math.min(bannerIndex, Math.max(count - 1, 0));

  const {
    activeModal,
    closeModal,
    handleBannerAction,
    handleBannerDismiss,
    isBannerActionValid
  } = useBannerActions(banners);

  const onIndexChange = useCallback(
    (index: number) => {
      dispatch(setBannerIndex(index));
    },
    [dispatch]
  );

  const { slidePos, isAnimating, goTo } = useBannerCarousel({
    count,
    onIndexChange
  });

  useTrackDynamicView(banners[currentIndex], ComponentType.BANNER);

  const extendedItems = useMemo(() => {
    if (count <= 1) return banners;
    return [banners[count - 1], ...banners, banners[0]];
  }, [banners, count]);

  if (count === 0) return null;

  const CustomModalFromAction = activeModal
    ? getCustomActionComponent(activeModal)
    : undefined;

  const offset = count <= 1 ? 0 : slidePos;

  return (
    <div
      className="fixed right-0 z-20 bg-white"
      style={{
        top: topBannersOffset + NAVBAR_HEIGHT,
        left: SIDEBAR_WIDTH,
        height: BANNER_HEIGHT
      }}
    >
      <div style={{ height: BANNER_HEIGHT, overflow: 'hidden' }}>
        <div
          className="flex w-full flex-nowrap will-change-transform"
          style={{
            transform: `translateX(-${offset * 100}%)`,
            transition: isAnimating
              ? `transform ${SLIDE_DURATION_MS}ms ease-in-out`
              : 'none',
            height: BANNER_HEIGHT
          }}
        >
          {extendedItems.map((banner, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${banner.id}-${idx}`}
              className="min-w-full"
            >
              <DynamicBanner
                instance={banner}
                showButton={isBannerActionValid(banner.id)}
                onAction={() => handleBannerAction(banner.id)}
                onDismiss={() => handleBannerDismiss(banner.id)}
              />
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <CarouselDots
          banners={banners}
          currentIndex={currentIndex}
          goTo={goTo}
        />
      )}

      {CustomModalFromAction && <CustomModalFromAction onClose={closeModal} />}
    </div>
  );
};

export default DynamicBannerCarousel;
